import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { ZoomIn, ZoomOut, Maximize2, CornerUpLeft, List } from 'lucide-react'
import { RoleWithMember, Team, OrgChartVersion } from '../../services/orgChartService'
import { RoleCard } from './RoleCard'
import { OrgChartSwitcher } from './OrgChartSwitcher'

interface OrgChartViewProps {
  roles: RoleWithMember[]
  teams: Team[]
  isAdmin: boolean
  orgCharts: OrgChartVersion[]
  activeChartId: string | null
  chartsBusy?: boolean
  onSelectChart: (chartId: string) => void
  onCreateDraft: () => void
  onPromoteToLive: () => void
  onRenameChart: (chartId: string, name: string) => void
  onDeleteDraft: (chartId: string) => void
  onManageRoles: () => void
  onCreateRole: (parentId: string | null) => void
  onEditRole: (role: RoleWithMember) => void
  onCopyRole: (role: RoleWithMember) => void
  onAssignPerson: (role: RoleWithMember) => void
  onViewPositionDescription: (role: RoleWithMember) => void
}

const CARD_WIDTH = 288 // w-72 = 18rem = 288px
const CARD_GAP = 32 // gap-8 = 2rem = 32px
const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5]
const DEFAULT_ZOOM = 1
const MIN_ZOOM = 0.1
const MAX_ZOOM = 2

export const OrgChartView: React.FC<OrgChartViewProps> = ({
  roles,
  teams,
  isAdmin,
  orgCharts,
  activeChartId,
  chartsBusy = false,
  onSelectChart,
  onCreateDraft,
  onPromoteToLive,
  onRenameChart,
  onDeleteDraft,
  onManageRoles,
  onCreateRole,
  onEditRole,
  onCopyRole,
  onAssignPerson,
  onViewPositionDescription
}) => {
  // Start with all roles expanded
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(() => {
    return new Set(roles.map(r => r.id))
  })

  /** When set, render this role as the chart root (hide ancestors). */
  const [focusRootId, setFocusRootId] = useState<string | null>(null)

  // Refs for measuring content and container
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Zoom state
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  /** Pixel offset applied with fit-to-view so the chart is centered in the viewport. */
  const [fitOffset, setFitOffset] = useState({ x: 0, y: 0 })

  const zoomIn = () => {
    setFitOffset({ x: 0, y: 0 })
    const currentIndex = ZOOM_LEVELS.indexOf(zoom)
    if (currentIndex >= 0 && currentIndex < ZOOM_LEVELS.length - 1) {
      setZoom(ZOOM_LEVELS[currentIndex + 1])
      return
    }
    // Not on a preset (e.g. after fit-to-view) — step up to the next higher preset
    if (zoom < MAX_ZOOM) {
      const nextLevel = ZOOM_LEVELS.find(l => l > zoom)
      setZoom(nextLevel ?? Math.min(zoom + 0.25, MAX_ZOOM))
    }
  }

  const zoomOut = () => {
    setFitOffset({ x: 0, y: 0 })
    const currentIndex = ZOOM_LEVELS.indexOf(zoom)
    if (currentIndex > 0) {
      setZoom(ZOOM_LEVELS[currentIndex - 1])
      return
    }
    // Not on a preset — step down to the next lower preset
    if (zoom > MIN_ZOOM) {
      const prevLevel = [...ZOOM_LEVELS].reverse().find(l => l < zoom)
      setZoom(prevLevel ?? Math.max(zoom - 0.25, MIN_ZOOM))
    }
  }

  const resetZoom = () => {
    setFitOffset({ x: 0, y: 0 })
    setZoom(DEFAULT_ZOOM)
  }

  const fitToView = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return

    const content = contentRef.current
    const container = containerRef.current
    
    const containerWidth = container.clientWidth - 32
    const containerHeight = container.clientHeight - 32
    
    // Measure unscaled content size
    const currentTransform = content.style.transform
    content.style.transform = 'scale(1)'
    const contentWidth = content.scrollWidth
    const contentHeight = content.scrollHeight
    content.style.transform = currentTransform
    
    if (contentWidth <= 0 || contentHeight <= 0) return

    const zoomX = containerWidth / contentWidth
    const zoomY = containerHeight / contentHeight
    const fitZoom = Math.min(zoomX, zoomY, MAX_ZOOM)
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(fitZoom, MAX_ZOOM))
    const roundedZoom = Math.round(clampedZoom * 100) / 100

    // Center any leftover space when one axis fits tighter than the other
    const scaledW = contentWidth * roundedZoom
    const scaledH = contentHeight * roundedZoom
    const offsetX = Math.max(0, (container.clientWidth - scaledW) / 2)
    const offsetY = Math.max(0, (container.clientHeight - scaledH) / 2)

    setZoom(roundedZoom)
    setFitOffset({ x: offsetX, y: offsetY })

    // Reset scroll so the centered transform is what you see
    requestAnimationFrame(() => {
      if (!containerRef.current) return
      containerRef.current.scrollLeft = 0
      containerRef.current.scrollTop = 0
    })
  }, [])

  // Auto fit to view on initial load when roles are available
  const [hasAutoFitted, setHasAutoFitted] = useState(false)
  useEffect(() => {
    if (!hasAutoFitted && roles.length > 0 && containerRef.current && contentRef.current) {
      // Small delay to ensure content is rendered
      const timer = setTimeout(() => {
        fitToView()
        setHasAutoFitted(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [roles.length, hasAutoFitted, fitToView])

  // Update expanded roles when new roles are added (keep them expanded)
  useEffect(() => {
    setExpandedRoles(prev => {
      const next = new Set(prev)
      roles.forEach(r => next.add(r.id))
      return next
    })
  }, [roles])

  // Clear focus if the focused role was deleted
  useEffect(() => {
    if (focusRootId && !roles.some(r => r.id === focusRootId)) {
      setFocusRootId(null)
    }
  }, [roles, focusRootId])

  // Build hierarchy: group roles by their parent
  const rolesByParent = useMemo(() => {
    const map = new Map<string | null, RoleWithMember[]>()
    
    for (const role of roles) {
      const parentId = role.reportsToRoleId
      if (!map.has(parentId)) {
        map.set(parentId, [])
      }
      map.get(parentId)!.push(role)
    }

    // Sort each group by order then title (grouping into rows happens at render)
    map.forEach((roleList) => {
      roleList.sort((a, b) => {
        const orderDiff = (b.order ?? 0) - (a.order ?? 0)
        if (orderDiff !== 0) return orderDiff
        return a.title.localeCompare(b.title)
      })
    })

    return map
  }, [roles])

  const rolesById = useMemo(() => {
    return new Map(roles.map(r => [r.id, r]))
  }, [roles])

  const focusRoot = focusRootId ? rolesById.get(focusRootId) ?? null : null

  const handleFocusSubtree = (roleId: string) => {
    // Double-click focused root again → show full org
    if (focusRootId === roleId) {
      setFocusRootId(null)
    } else {
      setFocusRootId(roleId)
      // Expand the focused subtree
      setExpandedRoles(prev => {
        const next = new Set(prev)
        next.add(roleId)
        const stack = [...(rolesByParent.get(roleId) || [])]
        while (stack.length > 0) {
          const child = stack.pop()!
          next.add(child.id)
          stack.push(...(rolesByParent.get(child.id) || []))
        }
        return next
      })
    }
    // Re-fit after layout updates
    setTimeout(() => fitToView(), 50)
  }

  const clearFocus = () => {
    setFocusRootId(null)
    setTimeout(() => fitToView(), 50)
  }

  const toggleExpand = (roleId: string) => {
    setExpandedRoles(prev => {
      const next = new Set(prev)
      if (next.has(roleId)) {
        next.delete(roleId)
      } else {
        next.add(roleId)
      }
      return next
    })
  }

  /** Group peers into horizontal rows by order; higher order rows first (above). */
  const groupPeersByOrder = (peers: RoleWithMember[]): RoleWithMember[][] => {
    const byOrder = new Map<number, RoleWithMember[]>()
    for (const peer of peers) {
      const o = peer.order ?? 0
      if (!byOrder.has(o)) byOrder.set(o, [])
      byOrder.get(o)!.push(peer)
    }
    return [...byOrder.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([, group]) => group)
  }

  const getRowWidth = (row: RoleWithMember[]): number => {
    if (row.length === 0) return CARD_WIDTH
    const childrenWidth = row.reduce((sum, child) => sum + getSubtreeWidth(child.id), 0)
    const gaps = (row.length - 1) * CARD_GAP
    return childrenWidth + gaps
  }

  // Width is the widest peer-order row under this role
  const getSubtreeWidth = (roleId: string): number => {
    const children = rolesByParent.get(roleId) || []
    const isExpanded = expandedRoles.has(roleId)
    
    if (!isExpanded || children.length === 0) {
      return CARD_WIDTH
    }
    
    const rows = groupPeersByOrder(children)
    return Math.max(CARD_WIDTH, ...rows.map(getRowWidth))
  }

  const LINE_CLASS = 'bg-gray-700'
  const LINE_THICK = 3 // px

  const renderRoleCard = (peer: RoleWithMember) => {
    const children = rolesByParent.get(peer.id) || []
    const hasChildren = children.length > 0
    return (
      <div className="w-72">
        <RoleCard
          role={peer}
          teams={teams}
          isAdmin={isAdmin}
          isExpanded={expandedRoles.has(peer.id)}
          onToggleExpand={
            hasChildren ? () => toggleExpand(peer.id) : undefined
          }
          onEdit={() => onEditRole(peer)}
          onCopyRole={() => onCopyRole(peer)}
          onAssignPerson={() => onAssignPerson(peer)}
          onAddColleague={() => onCreateRole(peer.reportsToRoleId)}
          onAddDirectReport={() => onCreateRole(peer.id)}
          onFocusSubtree={() => handleFocusSubtree(peer.id)}
          onViewPositionDescription={() => onViewPositionDescription(peer)}
        />
      </div>
    )
  }

  /**
   * One horizontal band of peers (same order). Nested reports sit in the
   * grid row above; peer cards share a locked baseline.
   */
  const renderAlignedPeerRow = (row: RoleWithMember[]) => {
    const columns = row.map(peer => ({
      peer,
      width: getSubtreeWidth(peer.id),
      children: rolesByParent.get(peer.id) || [],
      isExpanded: expandedRoles.has(peer.id),
    }))

    return (
      <div
        className="grid justify-center"
        style={{
          gridTemplateColumns: columns
            .map(c => `${Math.max(CARD_WIDTH, c.width)}px`)
            .join(' '),
          columnGap: CARD_GAP,
          rowGap: CARD_GAP,
        }}
      >
        {columns.map(({ peer, children, isExpanded }) => (
          <div
            key={`desc-${peer.id}`}
            className="flex flex-col items-center justify-end min-w-0"
          >
            {isExpanded && children.length > 0
              ? renderReports(children)
              : null}
          </div>
        ))}
        {columns.map(({ peer }) => (
          <div
            key={`card-${peer.id}`}
            className="flex justify-center w-full min-w-0"
          >
            {renderRoleCard(peer)}
          </div>
        ))}
      </div>
    )
  }

  /** T-connector (or single stem) from a flat peer row down to the parent. */
  const renderFlatConnector = (row: RoleWithMember[]) => {
    if (row.length === 0) return null
    const firstWidth = getSubtreeWidth(row[0].id)
    const lastWidth = getSubtreeWidth(row[row.length - 1].id)

    if (row.length === 1) {
      return (
        <div
          className={`flex-shrink-0 ${LINE_CLASS}`}
          style={{ width: LINE_THICK, height: 32 }}
        />
      )
    }

    return (
      <div className="relative w-full flex-shrink-0" style={{ height: 40 }}>
        <div
          className="absolute top-0 left-0 right-0 flex justify-center"
          style={{ gap: CARD_GAP }}
        >
          {row.map(child => (
            <div
              key={`drop-${child.id}`}
              className="flex justify-center"
              style={{ width: getSubtreeWidth(child.id) }}
            >
              <div
                className={LINE_CLASS}
                style={{ width: LINE_THICK, height: 20 }}
              />
            </div>
          ))}
        </div>
        <div
          className={`absolute ${LINE_CLASS}`}
          style={{
            top: 17,
            height: LINE_THICK,
            left: firstWidth / 2,
            right: lastWidth / 2,
          }}
        />
        <div
          className={`absolute left-1/2 ${LINE_CLASS}`}
          style={{
            top: 17,
            bottom: 0,
            width: LINE_THICK,
            transform: 'translateX(-50%)',
          }}
        />
      </div>
    )
  }

  /**
   * Direct reports of one person.
   * - One order level → flat T-connector row
   * - Multiple order levels → outlined "block report" (peers at different
   *   levels, not reporting to each other), single stem into the parent
   */
  const renderReports = (peers: RoleWithMember[]) => {
    const rows = groupPeersByOrder(peers)
    if (rows.length === 0) return null

    const isBlock = rows.length > 1

    if (!isBlock) {
      const row = rows[0]
      return (
        <div
          className="flex flex-col items-center"
          style={{ width: Math.max(CARD_WIDTH, getRowWidth(row)) }}
        >
          {renderAlignedPeerRow(row)}
          {renderFlatConnector(row)}
        </div>
      )
    }

    return (
      <div className="flex flex-col items-center">
        <div
          className="rounded-xl border-2 border-gray-300"
          style={{
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: CARD_GAP,
          }}
        >
          {rows.map(row => (
            <div
              key={row.map(r => r.id).join('-')}
              style={{ width: Math.max(CARD_WIDTH, getRowWidth(row)) }}
            >
              {renderAlignedPeerRow(row)}
            </div>
          ))}
        </div>
        {/* Single stem from the block into the parent */}
        <div
          className={`flex-shrink-0 ${LINE_CLASS}`}
          style={{ width: LINE_THICK, height: 32 }}
        />
      </div>
    )
  }

  // Roots: full chart (no parent) or focused role as sole root
  const rootRoles = focusRoot
    ? [focusRoot]
    : (rolesByParent.get(null) || [])

  const focusLabel = focusRoot
    ? (focusRoot.assignedMember?.name.fullName || focusRoot.title)
    : null

  return (
    <div className="min-h-[600px]">
      {/* Chart switcher (LHS, admins) + zoom controls (RHS) */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isAdmin && (
            <OrgChartSwitcher
              charts={orgCharts}
              activeChartId={activeChartId}
              isAdmin={isAdmin}
              busy={chartsBusy}
              onSelect={onSelectChart}
              onCreateDraft={onCreateDraft}
              onPromoteToLive={onPromoteToLive}
              onRename={onRenameChart}
              onDeleteDraft={onDeleteDraft}
            />
          )}
          {focusRoot && (
            <div className="flex items-center gap-3 min-w-0">
              <p className="text-sm text-gray-600 truncate">
                Viewing org from{' '}
                <span className="font-medium text-gray-900">{focusLabel}</span>
                {' '}down
              </p>
              <button
                type="button"
                onClick={clearFocus}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <CornerUpLeft className="w-4 h-4" />
                Show full org
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isAdmin && (
            <button
              type="button"
              onClick={onManageRoles}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              title="Manage role catalog"
            >
              <List className="w-4 h-4" />
              Manage roles
            </button>
          )}
          <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-2 py-1">
            <button
              onClick={zoomOut}
              disabled={zoom <= ZOOM_LEVELS[0]}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors min-w-[3.5rem]"
              title="Reset zoom"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={zoomIn}
              disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-gray-200" />
            <button
              onClick={fitToView}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Fit to view"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Org Chart */}
      <div 
        ref={containerRef}
        className="overflow-auto pb-4 scrollbar-visible border border-gray-200 rounded-lg bg-gray-50" 
        style={{ maxHeight: 'calc(100vh - 300px)' }}
      >
        <div 
          ref={contentRef}
          className="inline-flex justify-center min-w-full py-8 px-4 transition-transform duration-200 origin-top-left"
          style={{
            transform: `translate(${fitOffset.x}px, ${fitOffset.y}px) scale(${zoom})`,
          }}
        >
          {renderReports(rootRoles)}
        </div>
      </div>

      {/* Teams Legend */}
      {teams.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm">
          <span className="text-gray-500">Teams:</span>
          {teams.map(team => (
            <div key={team.id} className="flex items-center space-x-1">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: team.color }}
              />
              <span className="text-gray-700">{team.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
